import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Merge, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

/** Roll `chrome.exe` / `Google Chrome` / `chrome.google.com` up under one name
 *  so their time and classification stop being split three ways. */
export function ProdAppMergeDialog({ open, onOpenChange, query, onMerged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [pick, setPick] = useState({});      // group key -> chosen canonical
  const [ticked, setTicked] = useState([]);  // group keys queued for bulk merge

  const load = () => {
    setLoading(true);
    api.get(`/productivity/apps/duplicates?${query}`)
      .then(({ data: d }) => {
        setData(d);
        setPick(Object.fromEntries((d.groups || []).map((g) => [g.key, g.suggested_canonical])));
        setTicked([]);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not scan for duplicates"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, query]);

  const merge = async (g) => {
    const canonical = pick[g.key];
    const variants = g.members.map((m) => m.name).filter((n) => n !== canonical);
    if (!variants.length) return;
    setBusy(g.key);
    try {
      await api.post("/productivity/apps/merge", { canonical, variants });
      toast.success(`Merged ${variants.length} name(s) into “${canonical}”`);
      load();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Merge failed");
    } finally { setBusy(""); }
  };

  const mergeTicked = async () => {
    const chosen = (data?.groups || []).filter((g) => ticked.includes(g.key));
    if (!chosen.length) return;
    if (!window.confirm(
      `Merge ${chosen.length} group${chosen.length === 1 ? "" : "s"}? Each group's other names will `
      + "point at the one you've selected. You can undo any of them afterwards.")) return;
    setBusy("bulk");
    try {
      const { data: r } = await api.post("/productivity/apps/merge-bulk", {
        groups: chosen.map((g) => ({
          canonical: pick[g.key],
          variants: g.members.map((m) => m.name).filter((n) => n !== pick[g.key]),
        })),
      });
      toast.success(`Merged ${r.merged} name(s) across ${r.groups} group(s)`);
      load();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk merge failed");
    } finally { setBusy(""); }
  };

  const unmerge = async (name) => {
    setBusy(name);
    try {
      await api.post("/productivity/apps/unmerge", { names: [name] });
      toast.success(`“${name}” is separate again`);
      load();
      onMerged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Un-merge failed");
    } finally { setBusy(""); }
  };

  const safeKeys = (data?.groups || []).filter((g) => g.confidence !== "low").map((g) => g.key);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="prod-merge-dialog">
        <DialogHeader>
          <DialogTitle>Merge duplicate app names</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          The monitoring tool reports the same application under several names. Merging points them all at one
          name, so their time adds up and one classification covers the lot. Nothing is merged until
          you press Merge.
        </p>

        {loading ? (
          <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-500" /></div>
        ) : !data ? null : (
          <>
            {data.already_merged?.length > 0 && (
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3" data-testid="prod-merge-existing">
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Already merged ({data.already_merged.length})
                </div>
                <div className="space-y-1">
                  {data.already_merged.map((m) => (
                    <div key={m.name} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-slate-600 dark:text-slate-300">{m.name}</span>
                      <span className="text-slate-400 dark:text-slate-400">→</span>
                      <span className="font-mono font-medium text-slate-800 dark:text-slate-100">{m.canonical}</span>
                      <button type="button" disabled={busy === m.name} onClick={() => unmerge(m.name)}
                        data-testid={`prod-unmerge-${m.name}`}
                        className="ml-auto inline-flex items-center gap-1 text-slate-400 dark:text-slate-400 hover:text-rose-600">
                        <Undo2 className="h-3 w-3" /> undo
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!data.groups.length ? (
              <p className="py-8 text-center text-sm text-slate-400 dark:text-slate-400" data-testid="prod-merge-empty">
                No duplicate-looking names in this date range.
              </p>
            ) : (
              <>
                <div className="sticky top-0 z-10 -mx-1 flex flex-wrap items-center gap-2 border-b border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 px-1 py-2"
                  data-testid="prod-merge-bulkbar">
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <input type="checkbox" data-testid="prod-merge-select-all"
                      checked={safeKeys.length > 0 && ticked.length === safeKeys.length}
                      onChange={(e) => setTicked(e.target.checked ? safeKeys : [])} />
                    Select all {safeKeys.length} safe
                  </label>
                  <span className="text-xs text-slate-400 dark:text-slate-400">
                    {ticked.length} queued
                    {data.low_confidence > 0 && (
                      <span className="ml-2 text-rose-600" data-testid="prod-merge-low-count">
                        {data.low_confidence} need a look — not auto-selected
                      </span>
                    )}
                  </span>
                  <Button size="sm" className="ml-auto" disabled={!ticked.length || busy === "bulk"}
                    onClick={mergeTicked} data-testid="prod-merge-selected">
                    {busy === "bulk" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                      : <Merge className="mr-1 h-3.5 w-3.5" />}
                    Merge {ticked.length || ""} selected
                  </Button>
                </div>
                <div className="space-y-3">
                {data.groups.map((g) => (
                  <div key={g.key} className="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                    data-testid={`prod-merge-group-${g.key.replace(/\s+/g, "-")}`}>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                        <input type="checkbox" checked={ticked.includes(g.key)}
                          data-testid={`prod-merge-tick-${g.key.replace(/\s+/g, "-")}`}
                          onChange={(e) => setTicked((t) => (e.target.checked
                            ? [...t, g.key] : t.filter((k) => k !== g.key)))} />
                        {g.members.length} names · {g.hms} combined
                        {g.confidence === "low" && (
                          <span className="ml-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700"
                            data-testid={`prod-merge-low-${g.key.replace(/\s+/g, "-")}`}>
                            check this one
                          </span>
                        )}
                        {g.classes.length > 1 && (
                          <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:text-amber-200">
                            classified inconsistently
                          </span>
                        )}
                      </label>
                      <Button size="sm" variant="outline" disabled={busy === g.key}
                        onClick={() => merge(g)} data-testid={`prod-merge-btn-${g.key.replace(/\s+/g, "-")}`}>
                        {busy === g.key ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                          : <Merge className="mr-1 h-3.5 w-3.5" />}
                        Merge
                      </Button>
                    </div>
                    {g.confidence === "low" && (
                      <p className="mb-2 text-[11px] text-rose-700">{g.confidence_note}</p>
                    )}
                    <div className="space-y-1">
                      {g.members.map((m) => (
                        <label key={m.name} className="flex cursor-pointer items-center gap-2 text-sm">
                          <input type="radio" name={`canon-${g.key}`} checked={pick[g.key] === m.name}
                            onChange={() => setPick((p) => ({ ...p, [g.key]: m.name }))}
                            data-testid={`prod-merge-pick-${m.name}`} />
                          <span className={`font-mono text-xs ${pick[g.key] === m.name ? "font-semibold text-slate-900 dark:text-slate-50" : "text-slate-600 dark:text-slate-300"}`}>
                            {m.name}
                          </span>
                          <span className="text-xs text-slate-400 dark:text-slate-400">{m.hms}</span>
                          <span className="text-[10px] uppercase text-slate-400 dark:text-slate-400">{m.cls}</span>
                        </label>
                      ))}
                    </div>
                    <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-400">
                      Keeping <b>{pick[g.key]}</b> — the others will point at it.
                    </p>
                  </div>
                ))}
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
